import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Article } from '../common/model/dto/article';
import { RecordColumn } from '../common/model/ui/record-list.type';
import DateUtils from '../common/utils/services/date-utils';
import StringUtils from '../common/utils/services/string-utils';
import { ArticleFields } from '../firebase/document-field.enums';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvArticleRowActions from '../components/UI/molecules/OvArticleRowActions';

export function useArticleListColumns(
  getNavigationState: () => unknown,
  onDelete: (articleId: string) => void
): RecordColumn<Article>[] {
  const { t } = useTranslation();

  return [
    {
      id: 'title',
      header: t('articles.fields.title'),
      sortKey: ArticleFields.title,
      cardRole: 'heading',
      renderCell: (article) => (
        <TitleWrapper>
          <Thumbnail
            width="64"
            height="50"
            loading="lazy"
            src={article.image_url}
            alt={article.title}
          />
          {StringUtils.displayValue(article.title)}
        </TitleWrapper>
      ),
    },
    {
      id: 'topic',
      header: t('articles.fields.topic'),
      sortKey: ArticleFields.topic,
      renderCell: (article) => article.topic,
    },
    {
      id: 'section',
      header: t('articles.fields.section'),
      sortKey: ArticleFields.section,
      renderCell: (article) => article.section,
    },
    {
      id: 'timing',
      header: t('articles.fields.timing'),
      sortKey: ArticleFields.timing,
      renderCell: (article) => article.timing,
    },
    {
      id: 'dpo',
      header: t('articles.fields.DPO'),
      sortKey: ArticleFields.days_in_post_ovulation,
      renderCell: (article) => article.days_in_post_ovulation ?? '-',
    },
    {
      id: 'modified-at',
      header: t('articles.fields.modifiedAt'),
      sortKey: ArticleFields.updated_at,
      renderCell: (article) =>
        DateUtils.formatDate(article.updated_at, 'DD/MM/YYYY HH:mm'),
    },
    {
      id: 'color',
      header: t('articles.fields.color'),
      sortKey: ArticleFields.color,
      renderCell: (article) => article.color,
    },
    {
      id: 'cta',
      header: t('articles.fields.cta'),
      renderCell: (article) => article.cta,
    },
    {
      id: 'operation',
      header: t('common.operation'),
      isHeaderHidden: true,
      renderCell: (article) => (
        <OvArticleRowActions article={article} onDelete={onDelete} />
      ),
    },
    {
      id: 'action',
      header: t('common.action'),
      isHeaderHidden: true,
      cardRole: 'footer',
      renderCell: (article, detailsPath = '') => (
        <OvPillLink
          to={detailsPath}
          state={getNavigationState()}
          aria-label={t('articles.detailsFor', { name: article.title })}
        >
          {t('common.actions.seeDetails')}
        </OvPillLink>
      ),
    },
  ];
}

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Thumbnail = styled.img`
  flex: none;
  max-width: 4rem;
  border-radius: 0.75rem;
  object-fit: cover;
`;
